import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";
import { useAccessChecker } from "../../utils/Functions/Authorization";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, useSortable, verticalListSortingStrategy, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus, DotsSixVertical, PencilSimple, Trash, SquaresFour, CaretDown, Layout,
} from "@phosphor-icons/react";
import { PageHeader, Button, Badge, Skeleton, EmptyState, Switch, cn } from "../../components/ui";
import SectionEditor from "./SectionEditor";
import { PAGES, SECTION_TYPES, typeLabel } from "./sectionTypes";

function SortableRow({ section, canManage, onEdit, onToggle, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = { transform: CSS.Translate.toString(transform), transition };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-2 rounded-xl border border-line bg-surface px-2.5 py-2.5 transition",
        isDragging ? "opacity-50" : "hover:border-line-strong",
        !section.is_active && "opacity-70"
      )}
    >
      <button {...attributes} {...listeners} className="grid h-8 w-8 shrink-0 cursor-grab touch-none place-items-center rounded-lg text-faint transition hover:bg-surface-2 hover:text-muted active:cursor-grabbing" aria-label="Drag" tabIndex={-1}>
        <DotsSixVertical size={16} weight="bold" />
      </button>

      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-line bg-surface-2 text-muted">
        <Layout size={16} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-ink">{section.title || typeLabel(section.type)}</span>
          {!section.is_active && <Badge tone="neutral">Hidden</Badge>}
        </div>
        <span className="text-xs text-muted">{typeLabel(section.type)}</span>
      </div>

      {canManage.update && <Switch checked={!!section.is_active} onChange={() => onToggle(section)} />}
      {canManage.update && (
        <button onClick={() => onEdit(section)} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink" aria-label="Edit">
          <PencilSimple size={16} />
        </button>
      )}
      {canManage.delete && (
        <button onClick={() => onDelete(section)} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-red-500/10 hover:text-red-600" aria-label="Delete">
          <Trash size={16} />
        </button>
      )}
    </div>
  );
}

export default function Customizer() {
  const { checkAccess } = useAccessChecker();
  const canManage = {
    create: checkAccess("customizer", "create"),
    update: checkAccess("customizer", "update"),
    delete: checkAccess("customizer", "delete"),
  };

  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(PAGES[0].key);
  const [addOpen, setAddOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const addRef = useRef(null);

  const currentPage = PAGES.find((p) => p.key === page) || PAGES[0];

  useEffect(() => {
    fetchSections();
  }, []);

  useEffect(() => {
    const onClick = (e) => addRef.current && !addRef.current.contains(e.target) && setAddOpen(false);
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const fetchSections = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/sections");
      setSections(res.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load sections");
    } finally {
      setLoading(false);
    }
  };

  const pageSections = useMemo(
    () => sections.filter((s) => s.page === page).sort((a, b) => a.position - b.position),
    [sections, page]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = pageSections.map((s) => s.id);
    const reordered = arrayMove(pageSections, ids.indexOf(active.id), ids.indexOf(over.id)).map((s, i) => ({ ...s, position: i }));
    setSections((prev) => prev.map((s) => reordered.find((r) => r.id === s.id) || s));
    axiosInstance
      .post("/sections/reorder", { items: reordered.map((s) => ({ id: s.id, position: s.position })) })
      .catch(() => {
        toast.error("Couldn't save the new order");
        fetchSections();
      });
  };

  const createSection = async (type) => {
    setAddOpen(false);
    try {
      const res = await axiosInstance.post("/sections", { page, type, title: typeLabel(type), content: {} });
      const created = res.data.data;
      setSections((prev) => [...prev, created]);
      setEditing(created);
      setEditorOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn't add section");
    }
  };

  const saveSection = async (payload) => {
    if (!editing) return;
    try {
      setSaving(true);
      const res = await axiosInstance.put(`/sections/${editing.id}`, payload);
      const updated = res.data.data;
      setSections((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      setEditorOpen(false);
      setEditing(null);
      toast.success("Section saved");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn't save section");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (section) => {
    try {
      const res = await axiosInstance.put(`/sections/${section.id}`, { is_active: !section.is_active });
      setSections((prev) => prev.map((s) => (s.id === section.id ? res.data.data : s)));
    } catch {
      toast.error("Couldn't update visibility");
    }
  };

  const deleteSection = async (section) => {
    if (!window.confirm(`Delete this ${typeLabel(section.type)} section?`)) return;
    try {
      await axiosInstance.delete(`/sections/${section.id}`);
      setSections((prev) => prev.filter((s) => s.id !== section.id));
    } catch {
      toast.error("Couldn't delete section");
    }
  };

  return (
    <div className="w-full p-6 text-start">
      <PageHeader
        title="Customizer"
        subtitle="Build and edit your storefront content — sections, text, images and links."
      />

      <div className="flex flex-col gap-5 lg:flex-row">
        {/* page selector */}
        <aside className="lg:w-56 lg:shrink-0">
          <div className="rounded-2xl border border-line bg-surface p-2">
            <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted">Pages</div>
            <nav className="space-y-0.5">
              {PAGES.map((p) => {
                const count = sections.filter((s) => s.page === p.key).length;
                return (
                  <button
                    key={p.key}
                    onClick={() => setPage(p.key)}
                    className={cn(
                      "flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition",
                      page === p.key ? "bg-accent/10 text-accent" : "text-muted hover:bg-surface-2 hover:text-ink"
                    )}
                  >
                    <span>{p.label}</span>
                    {count > 0 && <span className="text-xs text-faint">{count}</span>}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* sections */}
        <div className="min-w-0 flex-1">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-ink">{currentPage.label}</h2>
              <p className="text-xs text-muted">Drag to reorder · toggle to show/hide.</p>
            </div>
            {canManage.create && (
              <div className="relative" ref={addRef}>
                <Button icon={Plus} onClick={() => setAddOpen((o) => !o)}>
                  Add section <CaretDown size={14} className="ml-1" />
                </Button>
                {addOpen && (
                  <div className="absolute right-0 z-30 mt-1 max-h-80 w-60 overflow-y-auto rounded-xl border border-line bg-surface p-1 shadow-pop">
                    {currentPage.types.map((type) => (
                      <button
                        key={type}
                        onClick={() => createSection(type)}
                        className="flex w-full flex-col items-start rounded-lg px-3 py-2 text-left transition hover:bg-surface-2"
                      >
                        <span className="text-sm font-medium text-ink">{SECTION_TYPES[type]?.label || type}</span>
                        <span className="text-xs text-muted">{SECTION_TYPES[type]?.description}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-line px-3 py-3">
                  <Skeleton className="h-9 w-9 rounded-lg" />
                  <div className="flex-1 space-y-2"><Skeleton className="h-3.5 w-40" /><Skeleton className="h-3 w-24" /></div>
                  <Skeleton className="h-6 w-11 rounded-full" />
                </div>
              ))}
            </div>
          ) : pageSections.length === 0 ? (
            <EmptyState
              icon={SquaresFour}
              title="No sections yet"
              description={`Add your first section to the ${currentPage.label} page.`}
            />
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={pageSections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {pageSections.map((section) => (
                    <SortableRow
                      key={section.id}
                      section={section}
                      canManage={canManage}
                      onEdit={(s) => { setEditing(s); setEditorOpen(true); }}
                      onToggle={toggleActive}
                      onDelete={deleteSection}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      <SectionEditor
        open={editorOpen}
        section={editing}
        saving={saving}
        onClose={() => { setEditorOpen(false); setEditing(null); }}
        onSave={saveSection}
      />
    </div>
  );
}
